import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { selectActiveMembership, useCurrentUser } from '../../../lib/auth';
import { PlanBenefitsPanel, SubscriptionPlanOfferCard } from '../../onboarding';
import { SettingsQueryGate } from '../../settings/components/SettingsQueryGate';
import { CheckoutContact } from '../../subscriptionCheckout';
import { useSubscriptionPlans } from '../api/subscriptionApi';

export function SubscriptionUpgradeOffer(): ReactElement {
    const { t } = useTranslation();
    const plans = useSubscriptionPlans();
    const usesCredits = selectActiveMembership(useCurrentUser())?.usesCredits === true;
    const [isContactOpen, setIsContactOpen] = useState(false);

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Typography variant="subtitle1" component="h3">
                    {t('SETTINGS.SUBSCRIPTION.UPGRADE.TITLE')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t(
                        usesCredits
                            ? 'SETTINGS.SUBSCRIPTION.UPGRADE.DESCRIPTION_WITH_CREDITS'
                            : 'SETTINGS.SUBSCRIPTION.UPGRADE.DESCRIPTION',
                    )}
                </Typography>
            </Stack>

            <SettingsQueryGate
                query={plans}
                loading={
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                }>
                {(offeredPlans) => (
                    <Stack spacing={2} sx={{ alignItems: 'center' }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{ width: '100%', alignItems: 'stretch' }}>
                            {offeredPlans.map((plan, index) => (
                                <SubscriptionPlanOfferCard
                                    key={plan.uuid}
                                    plan={plan}
                                    index={index}
                                    actionLabel={t('SETTINGS.SUBSCRIPTION.UPGRADE.CONTACT_SUPPORT')}
                                    onChoose={() => setIsContactOpen(true)}
                                />
                            ))}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {t('ONBOARDING.SUBSCRIPTION.PRICES_EXCL_MWST')}
                        </Typography>
                    </Stack>
                )}
            </SettingsQueryGate>

            <PlanBenefitsPanel />

            <Dialog
                open={isContactOpen}
                onClose={() => setIsContactOpen(false)}
                maxWidth="xs"
                fullWidth
                aria-label={t('SETTINGS.SUBSCRIPTION.UPGRADE.CONTACT_SUPPORT')}>
                <DialogContent sx={{ pt: 4 }}>
                    <CheckoutContact />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsContactOpen(false)}>{t('ACTIONS.CLOSE')}</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
